// Pure, server-authoritative physics/collision helpers for GameSession's tick
// loop. No I/O, no DO state — kept pure so it's cheap to unit test.

import { PLAYER_HEIGHT, PLAYER_WIDTH } from "../level";
import type { GroundGap, Platform } from "../level";

export const GRAVITY = 1400; // px/sec^2
export const JUMP_VELOCITY = -520; // px/sec
export const MOVE_SPEED = 180; // px/sec
export const MAX_FALL_SPEED = 900; // px/sec
export const STOMP_BOUNCE_VELOCITY = -300; // px/sec

export type Rect = { x: number; y: number; width: number; height: number };

export type PlayerPhysicsState = {
	x: number;
	y: number;
	vx: number;
	vy: number;
	onGround: boolean;
};

export type PlayerInput = { left: boolean; right: boolean; jump: boolean };

const NO_INPUT: PlayerInput = { left: false, right: false, jump: false };

// A gap swallows the ground landing only when the player's full footprint
// clears it — i.e. you must jump clean over a pit, not just have your center
// past its far edge — matching how the edge visually reads in GameView.
function footprintClearOfGaps(x: number, groundGaps: readonly GroundGap[]): boolean {
	for (const gap of groundGaps) {
		if (x + PLAYER_WIDTH > gap.x && x < gap.x + gap.width) return false;
	}
	return true;
}

export function stepPlayer(
	state: PlayerPhysicsState,
	input: PlayerInput | undefined,
	platforms: readonly Platform[],
	groundY: number,
	worldWidth: number,
	dtSec: number,
	groundGaps: readonly GroundGap[] = [],
): PlayerPhysicsState {
	const { left, right, jump } = input ?? NO_INPUT;

	let vx = 0;
	if (left) vx -= MOVE_SPEED;
	if (right) vx += MOVE_SPEED;

	let vy = state.vy + GRAVITY * dtSec;
	if (vy > MAX_FALL_SPEED) vy = MAX_FALL_SPEED;
	if (jump && state.onGround) vy = JUMP_VELOCITY;

	const prevBottom = state.y + PLAYER_HEIGHT;

	let x = state.x + vx * dtSec;
	x = Math.max(0, Math.min(worldWidth - PLAYER_WIDTH, x));

	let y = state.y + vy * dtSec;
	let onGround = false;

	const newBottom = y + PLAYER_HEIGHT;
	if (vy >= 0 && prevBottom <= groundY && newBottom >= groundY && footprintClearOfGaps(x, groundGaps)) {
		y = groundY - PLAYER_HEIGHT;
		vy = 0;
		onGround = true;
	} else if (vy >= 0) {
		for (const platform of platforms) {
			const withinX = x + PLAYER_WIDTH > platform.x && x < platform.x + platform.width;
			if (!withinX) continue;
			if (prevBottom <= platform.y && newBottom >= platform.y) {
				y = platform.y - PLAYER_HEIGHT;
				vy = 0;
				onGround = true;
				break;
			}
		}
	}

	return { x, y, vx, vy, onGround };
}

export function stepPatrol(
	x: number,
	dir: 1 | -1,
	minX: number,
	maxX: number,
	speed: number,
	dtSec: number,
): { x: number; dir: 1 | -1 } {
	let nx = x + dir * speed * dtSec;
	let ndir = dir;
	if (nx <= minX) {
		nx = minX;
		ndir = 1;
	} else if (nx >= maxX) {
		nx = maxX;
		ndir = -1;
	}
	return { x: nx, dir: ndir };
}

export function seagullY(baseY: number, elapsedSec: number): number {
	return baseY + Math.sin(elapsedSec * 2) * 30;
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
	return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

// A stomp is a falling player whose bottom was above the enemy's top last
// tick and has now crossed into it — as opposed to a side/bottom hit, which
// is any other overlap.
export function isStomp(playerPrevBottom: number, playerVy: number, enemyTop: number): boolean {
	return playerVy > 0 && playerPrevBottom <= enemyTop + 6;
}

export function playerRect(x: number, y: number): Rect {
	return { x, y, width: PLAYER_WIDTH, height: PLAYER_HEIGHT };
}
